import Cookies from 'js-cookie';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useMount } from 'react-use';

export const sessionDataCookieName = 'user-session';

const getCookieExpDate = (expHours = 10): Date => {
  const date = new Date();
  date.setTime(date.getTime() + expHours * 60 * 60 * 1000);
  return date;
};
export interface SessionData {
  userId: string;
  email: string;
  permissions: string[];
}

type Session =
  | ({
      authenticated: true;
    } & SessionData)
  | {
      authenticated: false;
    };

type InternalSession = Session & {
  setSession: Dispatch<SetStateAction<Session>>;
};

const SessionContext = createContext<InternalSession>({
  authenticated: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSession: () => {},
});

export const useSetSession = () => {
  const { setSession } = useContext(SessionContext);

  return (sessionData: Session, expHours?: number) => {
    Cookies.set(sessionDataCookieName, JSON.stringify(sessionData), {
      expires: getCookieExpDate(expHours),
    });
    setSession(sessionData);
  };
};

export const useSession: () => Session = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setSession, ...rest } = useContext(SessionContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userSession = useMemo(() => rest, [rest.authenticated]);
  return userSession;
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session>({ authenticated: false });
  useMount(() => {
    if (!session.authenticated) {
      const sessionString = Cookies.get(sessionDataCookieName);
      if (sessionString) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const sessionData: SessionData = JSON.parse(sessionString);
          setSession({ authenticated: true, ...sessionData });
        } catch (error) {
          console.error('Cookie parsing error: ', error);
        }
      }
    }
  });
  return (
    <SessionContext.Provider value={{ ...session, setSession }}>
      {children}
    </SessionContext.Provider>
  );
};
